import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  Box,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

export default function BoardSelectorModal({
  open,
  onClose,
  configs = [],
  eventCategory,
  currentHeatKey,
  onOpenBoard,
  onAssign,
  onUnassign, // Nueva prop para desasignar
  heatDetails,
  waitModeStates = {}, // Estado de modo espera de los tableros (cambiado de pauseStates)
  onToggleWaitMode, // Función para alternar modo espera (cambiado de onTogglePause)
}) {
  const [filteredConfigs, setFilteredConfigs] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!open) return;

    // Filtrar por tipo y asignación
    let list = configs
      .filter(c => (c.boardType ?? c.settings?.boardType) === eventCategory)
      .filter(c => !c.settings?.assignedHeat || c.settings.assignedHeat === currentHeatKey);

    setFilteredConfigs(list);

    // Pre-seleccionar
    const assigned = list.find(c => c.settings?.assignedHeat === currentHeatKey);
    setSelectedId(assigned?.id || (list[0]?.id || ''));
  }, [open, configs, eventCategory, currentHeatKey]);

  const handleConfigSelection = (configId) => {
    setSelectedId(configId);
  };

  const handleOpenBoard = (configId) => {
    const cfg = configs.find(c => c.id === configId);
    if (cfg) {
      if (typeof onOpenBoard === 'function') onOpenBoard(cfg);
      else console.error('onOpenBoard no es una función');
    }
    onClose();
  };

  const handleAssign = () => {
    const cfg = filteredConfigs.find(c => c.id === selectedId);
    if (cfg) onAssign(cfg);
  };

  const handleUnassign = (configId) => {
    const cfg = filteredConfigs.find(c => c.id === configId);
    if (cfg && typeof onUnassign === 'function') {
      onUnassign(cfg);
    }
  };

  const handleToggleWaitMode = (configId, event) => {
    event.stopPropagation();
    if (typeof onToggleWaitMode === 'function') {
      onToggleWaitMode(configId);
    }
  };

  const selectedConfig = filteredConfigs.find(c => c.id === selectedId);
  const isSelectedAssigned = selectedConfig?.settings?.assignedHeat === currentHeatKey;
  const isActionEnabled = !!selectedId;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Seleccionar tablero</Typography>
          <Chip label={eventCategory} size="small" color="primary" variant="outlined" />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {filteredConfigs.length > 0 ? (
          <>
            <List>
              {filteredConfigs.map(cfg => {
                const isAssigned = cfg.settings?.assignedHeat === currentHeatKey;
                const isWaiting = waitModeStates[cfg.id] || false;
                
                return (
                  <ListItemButton
                    key={cfg.id}
                    selected={cfg.id === selectedId}
                    onClick={() => handleConfigSelection(cfg.id)}
                    sx={{ 
                      borderRadius: 1, 
                      mb: 1, 
                      border: isAssigned ? '2px solid' : '1px solid', 
                      borderColor: isAssigned ? 'primary.main' : 'divider',
                      position: 'relative'
                    }}
                  >
                    <ListItemIcon>
                      {isAssigned ? <CheckCircleIcon color="primary" /> : <AssignmentIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">{cfg.name}</Typography>
                          {isWaiting && isAssigned && (
                            <Chip 
                              label="En modo espera" 
                              size="small" 
                              color="warning" 
                              variant="filled"
                              icon={<HourglassEmptyIcon fontSize="small" />}
                              sx={{ 
                                height: 20,
                                animation: 'chipPulse 2s infinite',
                                '@keyframes chipPulse': {
                                  '0%': { opacity: 1 },
                                  '50%': { opacity: 0.7 },
                                  '100%': { opacity: 1 }
                                }
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" component="span">
                            Tipo: {cfg.boardType} | Tamaño: {cfg.settings?.tableWidth || 1000}×{cfg.settings?.tableHeight || 1200}
                          </Typography>
                          {isAssigned && (
                            <Chip 
                              label="Asignado a este heat" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1, height: 20 }} 
                            />
                          )}
                        </>
                      }
                    />
                    
                    {/* Botones de acción para tableros asignados */}
                    {isAssigned && (
                      <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                        {/* Botón de modo espera - solo visible para tableros asignados */}
                        <Tooltip title={isWaiting ? 'Modo espera activo' : 'Modo espera inactivo'}>
                          <IconButton
                            size="small"
                            color={isWaiting ? 'warning' : 'success'}
                            onClick={(e) => handleToggleWaitMode(cfg.id, e)}
                            sx={{
                              backgroundColor: isWaiting 
                                ? 'rgba(255, 152, 0, 0.15)' 
                                : 'rgba(76, 175, 80, 0.15)',
                              '&:hover': {
                                backgroundColor: isWaiting 
                                  ? 'rgba(255, 152, 0, 0.25)' 
                                  : 'rgba(76, 175, 80, 0.25)',
                              },
                              animation: isWaiting ? 'waitPulse 2s infinite' : 'none',
                              '@keyframes waitPulse': {
                                '0%': { 
                                  transform: 'scale(1)',
                                  boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.7)'
                                },
                                '50%': { 
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 0 0 4px rgba(255, 152, 0, 0)'
                                },
                                '100%': { 
                                  transform: 'scale(1)',
                                  boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)'
                                }
                              }
                            }}
                          >
                            {isWaiting ? <HourglassEmptyIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Abrir tablero">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenBoard(cfg.id);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Desasignar tablero">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnassign(cfg.id);
                            }}
                          >
                            <RemoveCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </ListItemButton>
                );
              })}
            </List>

            {/* Información adicional si hay tableros en modo espera */}
            {filteredConfigs.some(cfg => waitModeStates[cfg.id] && cfg.settings?.assignedHeat === currentHeatKey) && (
              <Box 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: 'rgba(255, 152, 0, 0.1)', 
                  borderRadius: 1,
                  border: '1px solid rgba(255, 152, 0, 0.3)',
                  animation: 'warningPulse 3s infinite',
                  '@keyframes warningPulse': {
                    '0%': { backgroundColor: 'rgba(255, 152, 0, 0.1)' },
                    '50%': { backgroundColor: 'rgba(255, 152, 0, 0.15)' },
                    '100%': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <HourglassEmptyIcon fontSize="small" color="warning" />
                  <Typography variant="body2" color="warning.main" fontWeight="bold">
                    Tableros en modo espera
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Los tableros en modo espera no actualizarán automáticamente hasta que se desactive el modo espera.
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Box textAlign="center" py={3}>
            <Typography color="textSecondary" gutterBottom>
              No hay tableros configurados para pruebas de tipo "{eventCategory}".
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Ve a "Gestión de Tableros" para crear uno.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        
        {/* Mostrar botón de asignar solo si el tablero seleccionado no está asignado */}
        {!isSelectedAssigned && (
          <Button 
            variant="contained" 
            onClick={handleAssign} 
            disabled={!isActionEnabled}
            startIcon={<AssignmentIcon />}
          >
            Asignar a este heat
          </Button>
        )}
        
        {/* Mostrar botón de desasignar solo si el tablero seleccionado está asignado */}
        {isSelectedAssigned && (
          <Button 
            variant="outlined" 
            color="error"
            onClick={() => handleUnassign(selectedId)} 
            disabled={!isActionEnabled}
            startIcon={<RemoveCircleIcon />}
          >
            Desasignar tablero
          </Button>
        )}

        {/* Botón de modo espera en los actions para el tablero seleccionado y asignado */}
        {isSelectedAssigned && selectedId && (
          <Button 
            variant="outlined" 
            color={waitModeStates[selectedId] ? 'warning' : 'success'}
            onClick={() => handleToggleWaitMode(selectedId, { stopPropagation: () => {} })} 
            disabled={!isActionEnabled}
            startIcon={waitModeStates[selectedId] ? <PlayArrowIcon /> : <HourglassEmptyIcon />}
            sx={{
              animation: waitModeStates[selectedId] ? 'buttonPulse 2s infinite' : 'none',
              '@keyframes buttonPulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.02)' },
                '100%': { transform: 'scale(1)' }
              }
            }}
          >
            {waitModeStates[selectedId] ? 'Desactivar espera' : 'Activar espera'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}